import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Plus, Edit, Trash2, Search, Eye, Calendar, RefreshCw, Newspaper } from 'lucide-react';
import api from '../../services/api';
import AdminLayout from '../../components/admin/AdminLayout';
import { useToast } from '../../components/ui/toast';

const NewsListPage = () => {
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [lastUpdate, setLastUpdate] = useState(new Date());
    const toast = useToast();

    useEffect(() => {
        fetchNews();

        // Real-time updates every 5 seconds
        const interval = setInterval(() => {
            fetchNews(true);
        }, 5000);

        return () => clearInterval(interval);
    }, []);

    const fetchNews = async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            const response = await api.news.getAll();
            if (response.ok) {
                const data = await response.json();
                setNews(data);
                setLastUpdate(new Date());
            }
        } catch (error) {
            console.error("Failed to fetch news", error);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const handleDelete = async (slug) => {
        if (window.confirm('Are you sure you want to delete this article?')) {
            try {
                const response = await api.news.delete(slug);
                if (response.ok) {
                    setNews(news.filter(item => item.slug !== slug));
                    toast.success('Article deleted successfully');
                } else {
                    toast.error('Failed to delete article');
                }
            } catch (error) {
                console.error("Delete error", error);
                toast.error('Error deleting article');
            }
        }
    };

    const handleRefresh = () => {
        fetchNews();
        toast.info('Refreshing articles...');
    };

    const filteredNews = news.filter(item =>
        item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <AdminLayout title="News & Events">
            <Helmet>
                <title>Manage News - Admin Dashboard</title>
            </Helmet>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/25">
                            <Newspaper className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">{news.length}</p>
                            <p className="text-sm text-gray-400">Total Articles</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/25">
                            <Eye className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">{news.filter(n => n.is_published).length}</p>
                            <p className="text-sm text-gray-400">Published</p>
                        </div>
                    </div>
                </div>
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/10">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/25">
                            <Edit className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-white">{news.filter(n => !n.is_published).length}</p>
                            <p className="text-sm text-gray-400">Drafts</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Action Bar */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div className="flex items-center gap-3">
                    <p className="text-gray-400 text-sm">
                        Last updated: {lastUpdate.toLocaleTimeString()}
                    </p>
                    <button
                        onClick={handleRefresh}
                        className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        title="Refresh"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                </div>
                <Link
                    to="/admin/news/new"
                    className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-5 py-2.5 rounded-xl font-semibold hover:from-emerald-400 hover:to-teal-400 transition-all shadow-lg shadow-emerald-500/25 flex items-center gap-2"
                >
                    <Plus className="w-5 h-5" />
                    Create Article
                </Link>
            </div>

            {/* Table Card */}
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
                {/* Toolbar */}
                <div className="p-4 border-b border-white/10 flex gap-4">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                        <input
                            type="text"
                            placeholder="Search articles..."
                            className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500/50"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* List */}
                {loading ? (
                    <div className="p-8 text-center text-gray-400">
                        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                        Loading articles...
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-white/5 text-gray-400 font-medium text-sm">
                                <tr>
                                    <th className="px-6 py-4">Title</th>
                                    <th className="px-6 py-4">Category</th>
                                    <th className="px-6 py-4">Date</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {filteredNews.length > 0 ? (
                                    filteredNews.map((item) => (
                                        <tr key={item.id || item.slug} className="hover:bg-white/5 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    {item.image_url ? (
                                                        <img
                                                            src={item.image_url}
                                                            alt=""
                                                            className="w-12 h-12 rounded-lg object-cover border border-white/10"
                                                            onError={(e) => e.target.style.display = 'none'}
                                                        />
                                                    ) : (
                                                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-teal-500/20 to-blue-500/20 flex items-center justify-center">
                                                            <Newspaper className="w-5 h-5 text-teal-400" />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <p className="font-semibold text-white line-clamp-1">{item.title}</p>
                                                        <p className="text-xs text-gray-500 line-clamp-1">{item.summary}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
                                                    {item.category || 'General'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-400">
                                                <div className="flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    {new Date(item.date || item.created_at).toLocaleDateString()}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${item.is_published
                                                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                                                    : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                                                    }`}>
                                                    {item.is_published ? '● Published' : '○ Draft'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex justify-end gap-1">
                                                    <Link
                                                        to={`/news/${item.slug}`}
                                                        target="_blank"
                                                        className="p-2 text-gray-400 hover:text-teal-400 hover:bg-teal-500/10 rounded-lg transition-colors"
                                                        title="View Live"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Link>
                                                    <Link
                                                        to={`/admin/news/edit/${item.slug}`}
                                                        className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </Link>
                                                    <button
                                                        onClick={() => handleDelete(item.slug)}
                                                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-12 text-center">
                                            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                                                <Newspaper className="w-8 h-8 text-gray-500" />
                                            </div>
                                            <p className="text-gray-400 mb-2">No articles found</p>
                                            <Link
                                                to="/admin/news/new"
                                                className="text-teal-400 hover:underline text-sm"
                                            >
                                                Create your first article →
                                            </Link>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default NewsListPage;
